import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from './assets/logo/pzm.jpg';
import 'bulma/css/bulma.min.css'; // Ensure Bulma is imported
import './Navbar.css';

// Międzyczasy (Zawodnicy / Statystyki) są w testach — strony działają pod
// bezpośrednim linkiem (/competitor/event-X, /rally-stats/event-X), ale
// celowo NIE pokazujemy ich w menu, dopóki ktoś świadomie nie przełączy tej
// flagi z powrotem na true.
const SHOW_SPLITS_NAV = false;

const Navbar = ({ EventID }) => {
  const location = useLocation();
  const [webLink, setWebLink] = useState('');
  const [showButton, setShowButton] = useState(false);
  const [hasStartLists, setHasStartLists] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const setWebLinkWithProtocol = (link) => {
    if (link.startsWith('http://') || link.startsWith('https://')) {
      setWebLink(link);
    } else {
      setWebLink('http://' + link);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsActive(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Strony bez kontekstu wydarzenia (Start, Archiwum, Polityka Prywatności) nie mają
    // EventID do wysłania — bez tej strażniczki request leciał z EventID:undefined i backend
    // odpowiadał 500 (błąd konsoli na każdym wejściu na te strony, patrz audyt responsywności).
    if (!EventID) {
      setShowButton(false);
      return;
    }
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/resultsWebAddresForEvent`;
    const fetchWebLink = async () => {
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'UYwkNH50yAFoHI1mR62E',
          },
          body: JSON.stringify({
            EventID: EventID,
          }),
        });

        const data = await response.json();

        if (response.status === 200 && data.length > 0) {
          setWebLinkWithProtocol(data[0].WebAddress);
          setShowButton(true);
        } else {
          setShowButton(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setShowButton(false);
      }
    };

    fetchWebLink();
  }, [EventID]);

  useEffect(() => {
    if (!EventID) return;
    const checkStartLists = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/public/eventStartLists?EventID=${EventID}`
        );
        const data = await res.json();
        setHasStartLists(Array.isArray(data) && data.length > 0);
      } catch {
        setHasStartLists(false);
      }
    };
    checkStartLists();
  }, [EventID]);

  useEffect(() => {
    setIsActive(false);
  }, [location.pathname]);

  const toggleMenu = () => setIsActive((prev) => !prev);
  const closeMenu = () => setIsActive(false);

  return (
    <>
      <nav className="navbar is-dark enhanced-navbar" role="navigation" aria-label="main navigation">
        <div className="navbar__container">
          <div className="navbar-brand">
            <Link to="/" className="navbar-item brand-link" onClick={closeMenu}>
              <img src={logo} alt="PZM" className="brand-logo" />
              <div className="brand-copy">
                <span className="brand-title">PZM</span>
                <span className="brand-subtitle">Results Online</span>
              </div>
            </Link>
            <button
              type="button"
              className={`navbar-burger burger ${isActive ? 'is-active' : ''}`}
              aria-label="toggle navigation"
              aria-expanded={isActive}
              aria-controls="main-navigation"
              onClick={toggleMenu}
            >
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
            </button>
          </div>

          <div className={`navbar-menu enhanced-menu ${isActive ? 'is-active' : ''}`} id="main-navigation">
            <div className="navbar-end">
              <Link
                to="/"
                className={`navbar-item home-link ${location.pathname === '/' ? 'is-active' : ''}`}
                onClick={closeMenu}
              >
                Start
              </Link>
              <a
                href="https://track-monitgps.pl"
                className="navbar-item"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
              >
                Tracking
              </a>
              {showButton && webLink && (
                <a href={webLink} className="navbar-item" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
                  Więcej Informacji
                </a>
              )}
              {(location.pathname.startsWith('/results') || location.pathname.startsWith('/penalties') || location.pathname.startsWith('/start-list') || location.pathname.startsWith('/splits') || location.pathname.startsWith('/competitor') || location.pathname.startsWith('/rally-stats')) && (
                <Link
                  to={`/penalties/${getEventIDFromPath(location.pathname) || getEventIDFromResult(location.pathname) || getEventIDFromStartList(location.pathname) || EventID}`}
                  className={`navbar-item ${location.pathname.startsWith('/penalties') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Kary
                </Link>
              )}
              {SHOW_SPLITS_NAV && EventID && (
                <>
                  <Link
                    to={`/competitor/event-${EventID}`}
                    className={`navbar-item ${location.pathname.startsWith('/competitor') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    Zawodnicy
                  </Link>
                  <Link
                    to={`/rally-stats/event-${EventID}`}
                    className={`navbar-item ${location.pathname.startsWith('/rally-stats') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    Statystyki
                  </Link>
                </>
              )}
              {hasStartLists && EventID && (
                <Link
                  to={`/start-list/event-${EventID}`}
                  className={`navbar-item ${location.pathname.startsWith('/start-list') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Lista startowa
                </Link>
              )}
              {location.pathname === '/' ? (
                <Link to="/about" className={`navbar-item ${location.pathname === '/about' ? 'active' : ''}`} onClick={closeMenu}>
                  Archiwum Wyników
                </Link>
              ) : EventID ? (
                <Link
                  to={`/results/event-${EventID}`}
                  className={`navbar-item ${location.pathname.startsWith('/results') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Wyniki
                </Link>
              ) : location.pathname.startsWith('/penalties') ? (
                <Link to={`/results/${getEventIDFromResult(location.pathname)}`} className="navbar-item" onClick={closeMenu}>
                  Wyniki
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </nav>
      <div className={`navbar-overlay ${isActive ? 'is-active' : ''}`} onClick={closeMenu}></div>
    </>
  );
};
// Function to extract EventID from pathname
const getEventIDFromPath = (pathname) => {
  const parts = pathname.split('/');
  const eventIDIndex = parts.findIndex(part => part === 'results') + 1;
  return parts[eventIDIndex];
};

const getEventIDFromResult = (pathname) => {
  const parts = pathname.split('/');
  const eventIDIndex = parts.findIndex(part => part === 'penalties') + 1;
  return parts[eventIDIndex];
};

const getEventIDFromStartList = (pathname) => {
  const parts = pathname.split('/');
  const eventIDIndex = parts.findIndex(part => part === 'start-list') + 1;
  return parts[eventIDIndex];
};

export default Navbar;
